import { XCircleIcon } from '@heroicons/react/24/outline';
import { ScriptMode } from '@packages/global';

/**
 * 按钮状态枚举
 * 根据 Pencil 设计 QkCwO 定义的 6 种状态
 */
export enum ScriptButtonState {
  /** 状态1: 未激活 + 已完成 → 显示「生成分镜」 */
  GENERATE_STORYBOARD = 'generate_storyboard',
  /** 状态2: 已激活（当前脚本）→ 显示「查看视频」 */
  VIEW_VIDEO = 'view_video',
  /** 状态3: 已激活（其他脚本）+ 已完成 → 显示「生成视频」 */
  GENERATE_VIDEO = 'generate_video',
  /** 状态4: 生成失败 */
  FAILED = 'failed',
  /** 状态5: 生成中 */
  GENERATING = 'generating',
  /** 状态6: 处理中（确认操作） */
  PROCESSING = 'processing',
}

/**
 * ScriptActionButtons 组件属性
 */
export interface ScriptActionButtonsProps {
  /** 按钮状态 */
  buttonState: ScriptButtonState;
  /** 已完成步骤数 */
  completedSteps: number;
  /** 总步骤数 */
  totalSteps: number;
  /** 脚本模式 */
  scriptMode?: ScriptMode;
  /** 错误信息（状态4时显示） */
  errorMessage?: string;
  /** 生成分镜回调 */
  onGenerateStoryboard?: () => void;
  /** 查看视频回调 */
  onViewVideo?: () => void;
  /** 生成视频回调 */
  onGenerateVideo?: () => void;
  /** 重新生成分镜回调（已有分镜时可用） */
  onRegenerateStoryboard?: () => void;
  /** 重新生成回调 */
  onRegenerate?: () => void;
  /** 重试回调（失败状态） */
  onRetry?: () => void;
  /** 强制重试回调（卡住状态） */
  onForceRetry?: () => void;
  /** 是否显示进度条（默认 true） */
  showProgress?: boolean;
}

/**
 * 加载图标组件
 */
function LoaderIcon({ className }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className || 'w-4 h-4'}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * ScriptActionButtons 组件
 * 
 * 根据脚本状态显示不同的操作按钮，实现 6 种状态：
 * 1. 未激活+已完成 → 显示「生成分镜」(fill=$primary)
 * 2. 已激活（当前脚本）→ 显示「查看视频」(fill=#16A34A)
 * 3. 已激活（其他脚本）+已完成 → 显示「生成视频」
 * 4. 生成失败 → 显示错误状态 (fill=rgba(127,29,29,0.5))
 * 5. 生成中 → 显示 loader (fill=rgba(255,92,0,0.2))
 * 6. 处理中 → 显示 loader (fill=$primary, opacity=0.5)
 * 
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5, 9.6
 */
export default function ScriptActionButtons({
  buttonState,
  completedSteps,
  totalSteps,
  scriptMode = ScriptMode.PROMPT,
  errorMessage,
  onGenerateStoryboard,
  onViewVideo,
  onGenerateVideo,
  onRegenerateStoryboard,
  onRegenerate,
  onRetry,
  onForceRetry,
  showProgress = true,
}: ScriptActionButtonsProps) {
  // 进度文本
  const progressText = `${completedSteps}/${totalSteps} 完成`;
  const allCompleted = completedSteps === totalSteps;

  // 根据脚本模式显示不同的按钮文本
  // FILE_SCRIPT 和 PROJECT_SCRIPT 模式使用"重新提取"
  const hasScriptContent = scriptMode === ScriptMode.FILE_SCRIPT || scriptMode === ScriptMode.PROJECT_SCRIPT;
  const regenerateText = hasScriptContent ? '重新提取' : '重新生成';

  // 渲染状态1: 生成分镜
  const renderGenerateStoryboard = () => (
    <button
      onClick={onGenerateStoryboard}
      disabled={!allCompleted}
      className={`
        w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold
        transition-colors cursor-pointer
        ${allCompleted
          ? 'bg-primary text-white hover:bg-primary-dark'
          : 'bg-primary/20 text-primary/50 cursor-not-allowed'
        }
      `}
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
      生成分镜
    </button>
  );

  // 渲染状态2: 查看视频
  const renderViewVideo = () => (
    <div className="flex flex-col gap-2">
      <button
        onClick={onViewVideo}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold
          bg-[#16A34A] text-white hover:bg-[#15803D] transition-colors cursor-pointer"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        查看视频
      </button>
      {onRegenerateStoryboard && (
        <button
          onClick={() => onRegenerateStoryboard()}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs
            text-gray-400 hover:text-gray-200 hover:bg-bg-elevated transition-colors cursor-pointer"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          重新生成分镜
        </button>
      )}
    </div>
  );

  // 渲染状态3: 生成视频
  const renderGenerateVideo = () => (
    <button
      onClick={onGenerateVideo}
      disabled={!allCompleted}
      className={`
        w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold
        transition-colors cursor-pointer
        ${allCompleted
          ? 'bg-primary text-white hover:bg-primary-dark'
          : 'bg-primary/20 text-primary/50 cursor-not-allowed'
        }
      `}
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
      生成视频
    </button>
  );

  // 渲染状态4: 生成失败
  const renderFailed = () => {
    // 处理错误信息：截取 "Failed to parse" 之前的内容，并限制长度
    const getDisplayError = (msg?: string): string => {
      if (!msg) return '生成失败，点击重试';
      let displayMsg = msg;
      if (displayMsg.includes('Failed to parse')) {
        const parts = displayMsg.split('Failed to parse');
        displayMsg = parts[0].trim() || '生成失败';
      }
      // 移除末尾的冒号和空格
      displayMsg = displayMsg.replace(/[:：\s]+$/, '');
      // 限制长度
      if (displayMsg.length > 30) {
        displayMsg = displayMsg.substring(0, 30) + '...';
      }
      return displayMsg || '生成失败，点击重试';
    };

    return (
      <button
        onClick={onRetry}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold
          bg-error/20 text-error cursor-pointer
          hover:bg-error/30 transition-colors"
        title={errorMessage}
      >
        <XCircleIcon className="w-5 h-5 flex-shrink-0" />
        <span className="truncate">{getDisplayError(errorMessage)}</span>
      </button>
    );
  };

  // 渲染状态5: 生成中
  const renderGenerating = () => (
    <div className="flex flex-col gap-2">
      <button
        disabled
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold
          bg-primary/20 text-primary cursor-not-allowed"
      >
        <LoaderIcon className="w-5 h-5 text-primary" />
        生成中...
      </button>
      {onForceRetry && (
        <button
          onClick={onForceRetry}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs
            text-gray-400 hover:text-warning hover:bg-warning/10 transition-colors cursor-pointer"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          卡住了？强制继续
        </button>
      )}
    </div>
  );

  // 渲染状态6: 处理中
  const renderProcessing = () => (
    <button
      disabled
      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold
        bg-primary text-white opacity-50 cursor-not-allowed"
    >
      <LoaderIcon className="w-5 h-5 text-white" />
      处理中...
    </button>
  );

  // 根据状态渲染对应按钮
  const renderButtons = () => {
    switch (buttonState) {
      case ScriptButtonState.GENERATE_STORYBOARD:
        return renderGenerateStoryboard();
      case ScriptButtonState.VIEW_VIDEO:
        return renderViewVideo();
      case ScriptButtonState.GENERATE_VIDEO:
        return renderGenerateVideo();
      case ScriptButtonState.FAILED:
        return renderFailed();
      case ScriptButtonState.GENERATING:
        return renderGenerating();
      case ScriptButtonState.PROCESSING:
        return renderProcessing();
      default:
        return renderGenerateStoryboard();
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {/* 进度指示器（可选） */}
      {showProgress && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">{progressText}</span>
          {/* 进度条 */}
          <div className="flex-1 mx-3 h-1 bg-border-subtle rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* 操作按钮 */}
      {renderButtons()}
    </div>
  );
}
